import { definitions } from 'interfaces'
import moment from 'moment'
import {
  ADMITTED_STATUS,
  API_URL,
  CANCELLED_STATUS,
  NO_STOCK_STATUS,
  ORDER_STATUS_ID_SUBSCRIPTION,
  PENALIZATION_STATUS,
  PENALIZATION_STATUS_TEXT,
  PENDING_REFUND_PAYMENT_STATUS_ID,
  PROMO_ORDER_PROCESSED_DAYS,
  REFERRAL_BENEFIT_STATUS_ACCEPTED,
  REFERRAL_BENEFIT_STATUS_PENDING,
  REFUND_TYPE,
  REQUESTED_REFUND_STATUS_ID,
  SEQURA_PAYMENT_METHOD_ID
} from './constants'
import { getVariable, handleDisableItem } from './supabaseApi'
import { supabaseClient } from './supabaseClient'
import supabaseSuper from './superClient'
import { MapSupDescriptionToRefund } from './utils'

export const processNewStatus = async (
  newStatusId: number,
  order: definitions['orders'] & {
    supplements?: definitions['supplements'][],
    refunds?: definitions['refunds'][],
    items?: definitions['items'][],
    user: definitions['users']
  },
  penalizationAmount: number = 0
) => {
  // console.log({ order })
  // sendLog({ msg: 'Starting postActions', extra: { orderId: order.id } }, 'info', 'orderPostActions')

  let totalPaidRefunds = 0

  if (order.refunds && order.refunds.length > 0) {
    for (const refund of order.refunds) {
      if (refund.type === REFUND_TYPE.client && !refund.deleted && [PENDING_REFUND_PAYMENT_STATUS_ID, REQUESTED_REFUND_STATUS_ID].includes(refund.status_id)) {
        await supabaseClient.from('refunds').update({ deleted: true }).eq('id', refund.id)
      } if (refund.type === REFUND_TYPE.client && !refund.deleted && [ADMITTED_STATUS].includes(refund.status_id)) {
        totalPaidRefunds += refund.amount
      }
    }
  }

  // Create a refund for the initial payment
  let totalRefundAmount = Math.abs(order.initial_payment! - totalPaidRefunds)

  if ([PENALIZATION_STATUS].includes(newStatusId)) totalRefundAmount -= penalizationAmount
  if (order?.subscription_cost > 0) totalRefundAmount -= order?.subscription_cost

  await supabaseClient.from('refunds').insert({
    order_id: order.id,
    description: 'Pago inicial',
    amount: order.status_id === ORDER_STATUS_ID_SUBSCRIPTION ? order.total : totalRefundAmount,
    type: REFUND_TYPE.client,
    updated_by: supabaseClient.auth.user()?.email
  })

  if (order.supplements && order.supplements.length > 0) {
    const supplements = order.supplements.filter(s => !s.deleted && s.status_id === ADMITTED_STATUS)

    for (const sup of supplements) {
      await supabaseClient.from('refunds').insert({
        order_id: order.id,
        description: (sup.description === null) ? 'Volumetrico' : MapSupDescriptionToRefund(sup.description!),
        amount: sup.supplement,
        type: REFUND_TYPE.client,
        updated_by: supabaseClient.auth.user()?.email,
        supplement_id: sup.id ?? null,
        parent_supplement_id: sup.parent ?? null
      })
    }
  }

  if ([CANCELLED_STATUS, NO_STOCK_STATUS].includes(newStatusId)) {
    let refundReferralBenefitId = 0

    // Return wallet balance changes if a wallet amount was used on the order and create a referral benefit if the order is cancelled or set as no stock
    if (order.wallet_amount_used > 0) {
      const variablePromoOrderProcessedDays = await getVariable(PROMO_ORDER_PROCESSED_DAYS)

      const expiredDate = new Date(Date.now())
      expiredDate.setDate(expiredDate.getDate() + parseInt(variablePromoOrderProcessedDays))

      const { data: newReferralBenefits, error: errorReferral } = await supabaseClient.from('referral_benefits')
        .insert({
          referrer_user_id: 0,
          referred_user_id: order?.user_id,
          benefit_amount: order?.wallet_amount_used,
          status: REFERRAL_BENEFIT_STATUS_ACCEPTED,
          referred_expired_at: expiredDate.toISOString(),
          referrer_expired_at: expiredDate.toISOString(),
          increased_by_syniva: false,
          processed_promo: order?.id,
          no_stock: (newStatusId === NO_STOCK_STATUS)
        })

      if (errorReferral) {
        console.log('Not Updated->', order.user_id)
      } else {
        refundReferralBenefitId = newReferralBenefits[0].id
        console.log('Updated->', order.user_id)
      }

      await supabaseClient.from('users')
        .update({ wallet_balance: order.user.wallet_balance + order.wallet_amount_used })
        .eq('id', order.user_id)
    }

    // We get the referral benefits related to this order to reverse the wallet balance changes
    const { data: referralBenefits } = await supabaseClient.from('referral_benefits')
      .select('*')
      .eq('processed_promo', order?.id)
      .not('id', 'eq', refundReferralBenefitId)

    if (referralBenefits && referralBenefits.length > 0) {
      const { data: user } = await supabaseClient.from('users')
        .select('id, wallet_balance')
        .eq('id', order?.user_id)
        .single()
      if (user) {
        const newWalletBalance = user.wallet_balance - referralBenefits[0].benefit_amount
        await supabaseSuper.from('users')
          .update({ wallet_balance: newWalletBalance })
          .eq('id', user.id)
      }
    }

    // Delete the referral benefits linked to this order
    await supabaseClient.from('referral_benefits')
      .delete()
      .eq('processed_promo', order?.id)
      .not('id', 'eq', refundReferralBenefitId)

    // We get the original referral benefits related to this order to reverse the wallet balance changes
    const { data: originalReferralBenefits } = await supabaseClient.from('referral_benefits')
      .select('*')
      .eq('order_id', order?.id)
      .single()

    if (originalReferralBenefits) {
      const { data: userReferrer } = await supabaseClient.from('users')
        .select('*')
        .eq('id', originalReferralBenefits.referrer_user_id)
        .single()
      if (userReferrer) {
        const newWalletBalance = userReferrer.wallet_balance - originalReferralBenefits.benefit_amount
        await supabaseClient.from('users')
          .update({ wallet_balance: newWalletBalance })
          .eq('id', userReferrer.id)
      }
    }

    // Reset referral benefit linked to this order
    await supabaseClient.from('referral_benefits')
      .update({
        order_id: null,
        referrer_expired_at: null,
        nif: null,
        phone: null,
        status: REFERRAL_BENEFIT_STATUS_PENDING
      })
      .eq('order_id', order?.id)
      .eq('status', REFERRAL_BENEFIT_STATUS_ACCEPTED)
      .gt('referrer_expired_at', new Date().toISOString())
      .not('nif', 'is', null)
      .not('phone', 'is', null)
  } else if ([PENALIZATION_STATUS].includes(newStatusId)) {
    await supabaseClient
      .from<definitions['Order']>('orders')
      .update({
        shipping_method_id: order.shipping_method_id,
        updated_by: supabaseClient.auth.user()?.email,
        discount: 0,
        wallet_amount_used: 0,
        custom_order_price: 0,
        shipping_price: 0
      })
      .eq('id', order!.id)

    const itemIds = order.items?.filter((item: any) => !item.disabled).map((item: any) => item.id) || []
    await handleDisableItem(itemIds, true, false, PENALIZATION_STATUS)

    // remove all supplements from the order to show only the penalization refund on invoice details
    await supabaseClient.from('supplements')
      .update({ deleted: true })
      .eq('order_id', order.id)
      .neq('description', PENALIZATION_STATUS_TEXT)

    await supabaseClient
      .from('supplements')
      .insert([{
        volumetric: false,
        order_id: order.id,
        status_id: ADMITTED_STATUS,
        description: PENALIZATION_STATUS_TEXT,
        supplement: penalizationAmount,
        updated_at: moment(),
        updated_by: supabaseClient.auth.user()?.email
      }])

    // if is a Sequra order, send the penalization to Sequra
    if (order.payment_method_id && order.payment_method_id === SEQURA_PAYMENT_METHOD_ID) {
      try {
        const res = await fetch(`${API_URL}/sequra/penalization/${order.id}/${newStatusId}`, { method: 'POST' })
        if ([200, 202, 204].includes(res.status)) {
          console.log('Done')
        }
      } catch (err) {
        console.error(err)
        // sendLog({ msg: 'Error updating values on Sequra, check body', error: err, extra: { orderId: order.id } }, 'error', 'orderPostActions')
      }
    }
  }

  // Set order status to the newStatusId
  await supabaseClient.from('orders').update({
    prev_status_id: order.status_id === ORDER_STATUS_ID_SUBSCRIPTION ? order.prev_status_id : order.status_id,
    status_id: order.status_id === ORDER_STATUS_ID_SUBSCRIPTION ? CANCELLED_STATUS : newStatusId,
    updated_by: supabaseClient.auth.user()?.email
  }).eq('id', order.id)

  // Generate a invoice
  // try {
  //   if (![PENALIZATION_STATUS].includes(newStatusId)) {
  //     window.open(`${API_URL}/refunds/pdf/${order.id}`, '_blank', 'noopener,noreferrer')
  //   }
  // } catch (error) {
  //   console.error({ msg: 'Error generating invoice after penalization' }, error)
  // }

  if ([CANCELLED_STATUS, NO_STOCK_STATUS].includes(newStatusId)) window.location.reload()
}
